import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { REALTIME_EVENTS } from '@adco-rooms/shared';
import { invalidationGroups } from '@/lib/api/query-client';
import { isAuthSettled, useAuth } from '@/lib/auth/auth-provider';
import { env } from '@/lib/env';
import { createSocket, type AppSocket, type RealtimeStatus } from '@/lib/realtime/socket';

/**
 * Realtime plumbing.
 *
 * Server events are treated as *invalidation signals*, never as the source of
 * truth: every event (and every reconnection) simply marks the affected query
 * groups as stale so TanStack Query refetches from the API.
 */

export interface RealtimeContextValue {
  socket: AppSocket | null;
  status: RealtimeStatus;
  /** Timestamp of the last successful connection, for the "sin conexión" UI. */
  lastConnectedAt: number | null;
  /** Subscribes the socket to the calendar rooms currently on screen. */
  subscribeRooms: (roomIds: readonly number[]) => void;
  unsubscribeRooms: (roomIds: readonly number[]) => void;
}

const RealtimeContext = createContext<RealtimeContextValue>({
  socket: null,
  status: 'idle',
  lastConnectedAt: null,
  subscribeRooms: () => undefined,
  unsubscribeRooms: () => undefined,
});

export function RealtimeProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const { status: authStatus, getToken } = useAuth();
  const [status, setStatus] = useState<RealtimeStatus>('idle');
  const [lastConnectedAt, setLastConnectedAt] = useState<number | null>(null);
  const socketRef = useRef<AppSocket | null>(null);
  const subscribedRoomsRef = useRef<Set<number>>(new Set());

  const invalidate = useCallback(
    (keys: ReturnType<(typeof invalidationGroups)['reservationChanged']>) => {
      for (const key of keys) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
    [queryClient],
  );

  useEffect(() => {
    if (!env.realtimeEnabled || !isAuthSettled(authStatus)) return;

    const socket = createSocket({ getToken });
    socketRef.current = socket;
    setStatus('connecting');

    const handleConnect = (): void => {
      setStatus('connected');
      setLastConnectedAt(Date.now());

      // Re-subscribe and re-sync: while disconnected we may have missed events.
      const rooms = [...subscribedRoomsRef.current];
      if (rooms.length > 0) socket.emit('calendar:subscribe', { roomIds: rooms });
      invalidate(invalidationGroups.reservationChanged());
    };

    const handleDisconnect = (): void => setStatus('reconnecting');
    const handleError = (): void => setStatus('offline');

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleError);
    socket.io.on('reconnect_attempt', () => setStatus('reconnecting'));

    socket.on(REALTIME_EVENTS.reservationCreated, () =>
      invalidate(invalidationGroups.reservationChanged()),
    );
    socket.on(REALTIME_EVENTS.reservationUpdated, () =>
      invalidate(invalidationGroups.reservationChanged()),
    );
    socket.on(REALTIME_EVENTS.reservationCancelled, () =>
      invalidate(invalidationGroups.reservationChanged()),
    );
    socket.on(REALTIME_EVENTS.reservationDeclined, () =>
      invalidate(invalidationGroups.reservationChanged()),
    );
    socket.on(REALTIME_EVENTS.roomUpdated, () => invalidate(invalidationGroups.roomChanged()));
    socket.on(REALTIME_EVENTS.roomAvailabilityChanged, () =>
      invalidate(invalidationGroups.availabilityChanged()),
    );

    socket.connect();

    return () => {
      socket.removeAllListeners();
      socket.io.off('reconnect_attempt');
      socket.disconnect();
      socketRef.current = null;
      setStatus('idle');
    };
  }, [authStatus, getToken, invalidate]);

  const subscribeRooms = useCallback((roomIds: readonly number[]) => {
    const added = roomIds.filter((id) => !subscribedRoomsRef.current.has(id));
    for (const id of roomIds) subscribedRoomsRef.current.add(id);
    if (added.length > 0 && socketRef.current?.connected) {
      socketRef.current.emit('calendar:subscribe', { roomIds: added });
    }
  }, []);

  const unsubscribeRooms = useCallback((roomIds: readonly number[]) => {
    const removed = roomIds.filter((id) => subscribedRoomsRef.current.delete(id));
    if (removed.length > 0 && socketRef.current?.connected) {
      socketRef.current.emit('calendar:unsubscribe', { roomIds: removed });
    }
  }, []);

  const value = useMemo<RealtimeContextValue>(
    () => ({
      socket: socketRef.current,
      status,
      lastConnectedAt,
      subscribeRooms,
      unsubscribeRooms,
    }),
    [status, lastConnectedAt, subscribeRooms, unsubscribeRooms],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtimeContext(): RealtimeContextValue {
  return useContext(RealtimeContext);
}
